import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';

import { MODAL_DATA, MODAL_REF, ModalRef } from '@services/components/ModalService/modal.ref';

import { RETURN_ORDER_REASONS } from '@constants/orders.constants';

export interface ReturnOrderModalData {
  orderCode: string;
}

/** Valor del radio "Otro"; nunca se manda al back, solo indica que se use customReason. */
const OTHER_REASON_OPTION = 'other';

/**
 * A diferencia del modal de cancelación, acá el motivo es **opcional**: se puede
 * confirmar la devolución sin elegir nada. Lo que se elija queda guardado en
 * `statusReason` y se ve después en el detalle del pedido.
 */
@Component({
  selector: 'app-return-order-modal',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './return-order-modal.component.html',
  styleUrl: './return-order-modal.component.css',
})
export class ReturnOrderModalComponent {
  private readonly modalRef = inject(MODAL_REF) as ModalRef<string>;
  private readonly data = inject<ReturnOrderModalData>(MODAL_DATA);
  private readonly fb = inject(FormBuilder);

  protected readonly orderCode = this.data?.orderCode ?? '';
  protected readonly reasonOptions = RETURN_ORDER_REASONS;
  protected readonly otherOptionValue = OTHER_REASON_OPTION;

  public readonly form = this.fb.nonNullable.group({
    reasonOption: [''],
    customReason: [''],
  });

  protected get isOtherSelected(): boolean {
    return this.form.controls.reasonOption.value === OTHER_REASON_OPTION;
  }

  public onCancel(): void {
    this.modalRef.close({ reason: 'cancel' });
  }

  public clearReason(): void {
    this.form.reset({ reasonOption: '', customReason: '' });
  }

  public onSubmit(): void {
    this.modalRef.close({ reason: 'confirm', data: this.buildReason() });
  }

  /**
   * `undefined` cuando no se registró motivo: así el back deja `statusReason`
   * en null en vez de guardar una cadena vacía.
   */
  private buildReason(): string | undefined {
    const { reasonOption, customReason } = this.form.getRawValue();

    if (!reasonOption) {
      return undefined;
    }

    if (reasonOption !== OTHER_REASON_OPTION) {
      return reasonOption;
    }

    return customReason.trim() || undefined;
  }
}
